import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { parseISO } from 'date-fns';
import { Save } from 'lucide-react';
import {
  createTaskSchema, MIN_IMPORTANT_ASSIGNEES,
  type CreateTaskInput, type PriorityLimitDetails, type TaskType,
} from '@pms/shared';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { TimePicker } from '@/components/ui/time-picker';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { tasksService } from '@/services/tasks.service';
import { getErrorMessage, getPriorityLimitDetails } from '@/lib/errors';
import { getTodayIST } from '@/lib/dateFormat';
import { ALL_TYPES, TYPE_META, WEEKDAY_LABELS, MONTH_LABELS } from './taskConstants';
import { DayChips } from './DayChips';
import { AssigneePicker } from './AssigneePicker';
import { PriorityLimitDialog } from './PriorityLimitDialog';
import { AssigneeSequenceList } from './AssigneeSequenceList';
import { VoiceNoteRecorder, type VoiceNoteDraft } from './VoiceNoteRecorder';
import { Switch } from '@/components/ui/switch';

interface CreateTaskModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MONTHLY_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);
const WEEKDAY_OPTIONS = WEEKDAY_LABELS.map((label, value) => ({ value, label }));
/** A periodic task runs every day in its range, so it always sends the full week. */
const EVERY_WEEKDAY = [0, 1, 2, 3, 4, 5, 6];

export function CreateTaskModal({ open, onOpenChange }: CreateTaskModalProps) {
  const qc = useQueryClient();
  const typeSettingsQ = useQuery({
    queryKey: ['tasks', 'config', 'task-types'],
    queryFn: () => tasksService.getTaskTypeSettings(),
    enabled: open,
  });
  // Only enabled task types are offered (admin can disable types in Task Config).
  const availableTypes = useMemo<TaskType[]>(() => {
    if (!typeSettingsQ.data) return ALL_TYPES;
    const enabled = new Set(typeSettingsQ.data.filter((t) => t.is_enabled).map((t) => t.task_type));
    return ALL_TYPES.filter((t) => enabled.has(t));
  }, [typeSettingsQ.data]);

  // parseISO gives local midnight, which is what DatePicker's `d < minDate` comparison
  // needs — a value carrying a time would disable today itself. Not memoised on purpose:
  // recomputing per render is trivial and keeps a tab left open overnight honest about
  // which day it is.
  const todayIST = parseISO(getTodayIST());

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [taskType, setTaskType] = useState<TaskType>('daily');
  const [isPriority, setIsPriority] = useState(false);
  const [isImportant, setIsImportant] = useState(false);
  const [reminder, setReminder] = useState(false);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('19:00');
  const [monthlyDay, setMonthlyDay] = useState(1);
  const [yearlyMonth, setYearlyMonth] = useState(1);
  const [yearlyDay, setYearlyDay] = useState(1);
  const [periodicStartDate, setPeriodicStartDate] = useState('');
  const [periodicEndDate, setPeriodicEndDate] = useState('');
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [assignees, setAssignees] = useState<string[]>([]);
  const [voiceNote, setVoiceNote] = useState<VoiceNoteDraft | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Deliberately NOT cleared by reset(): the form stays open behind this dialog, and
  // clearing it there would close the explanation the moment it appeared.
  const [priorityLimit, setPriorityLimit] = useState<PriorityLimitDetails | null>(null);

  function reset() {
    setTitle(''); setDescription(''); setTaskType('daily'); setIsPriority(false);
    setIsImportant(false);
    setReminder(false); setStartTime('09:00'); setEndTime('19:00');
    setMonthlyDay(1); setYearlyMonth(1); setYearlyDay(1);
    setPeriodicStartDate(''); setPeriodicEndDate('');
    setWeekdays([]); setAssignees([]); setVoiceNote(null); setErrors({});
  }

  const create = useMutation({
    mutationFn: async (input: CreateTaskInput) => {
      const result = await tasksService.create(input);
      // The recording is uploaded only now, so abandoning the form leaves nothing
      // behind on the server. A failure here must not read as "task not created" —
      // the task exists, so it is reported separately below.
      if (voiceNote) {
        try {
          await tasksService.uploadVoiceNote(result.id, voiceNote.blob, voiceNote.durationMs);
        } catch (e) {
          return { ...result, voiceNoteError: getErrorMessage(e) };
        }
      }
      return { ...result, voiceNoteError: null as string | null };
    },
    onSuccess: (result) => {
      if (result.voiceNoteError) {
        toast.warning(`Task created, but the voice note failed to upload — add it from Edit. (${result.voiceNoteError})`);
      } else {
        toast.success('Task created');
      }
      qc.invalidateQueries({ queryKey: ['tasks'] });
      reset();
      onOpenChange(false);
    },
    // The priority cap gets a dialog listing the blocking tasks; everything else is a
    // toast as before. Never both — one failure, one piece of feedback.
    onError: (e) => {
      const limit = getPriorityLimitDetails(e);
      if (limit) setPriorityLimit(limit);
      else toast.error(getErrorMessage(e));
    },
  });

  function submit() {
    const payload = {
      title, description: description || null, task_type: taskType,
      // Daily tasks are never priority; pinned here as well as in the UI so the payload
      // is correct no matter what state the form was left in.
      is_priority: taskType === 'daily' ? false : isPriority,
      is_important: isImportant, reminder_enabled: reminder,
      start_time: startTime || null, end_time: endTime || null,
      monthly_day: taskType === 'monthly' ? monthlyDay : null,
      yearly_month: taskType === 'yearly' ? yearlyMonth : null,
      yearly_day: taskType === 'yearly' ? yearlyDay : null,
      // Periodic ("One Time") is one occurrence on the start date, due on the end date.
      // The mode and full weekday set are still sent for rows without a start date,
      // where the engine returns false for a null periodic_mode.
      periodic_mode: taskType === 'periodic' ? ('weekdays' as const) : null,
      periodic_days: undefined,
      weekly_weekdays:
        taskType === 'weekly' ? weekdays
          : taskType === 'periodic' ? EVERY_WEEKDAY
            : undefined,
      periodic_start_date: taskType === 'periodic' ? periodicStartDate || null : null,
      periodic_end_date: taskType === 'periodic' ? periodicEndDate || null : null,
      assignee_ids: assignees,
    };
    const parsed = createTaskSchema.safeParse(payload);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0]?.toString() ?? 'form';
        if (!fe[key]) fe[key] = issue.message;
      }
      setErrors(fe);
      if (taskType === 'periodic' && (fe.periodic_start_date || fe.periodic_end_date)) {
        toast.error('Start Date and End Date are required for One Time tasks');
      }
      return;
    }
    setErrors({});
    create.mutate(parsed.data);
  }

  const yearlyDays = useMemo(() => Array.from({ length: 31 }, (_, i) => i + 1), []);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Task</DialogTitle>
          <DialogDescription>Create a new task and assign it to team members</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Task Title <span className="text-rose-500">*</span></Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter task title" />
            {errors.title && <p className="text-xs text-rose-500">{errors.title}</p>}
          </div>

          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Enter task description" rows={3} />
          </div>

          <VoiceNoteRecorder
            value={voiceNote}
            onChange={setVoiceNote}
            disabled={create.isPending}
          />

          <div className="space-y-1.5">
            <Label>Task Type <span className="text-rose-500">*</span></Label>
            {/* Switching to daily clears Priority as well as hiding it — otherwise a flag
                ticked while the task was weekly would survive unseen into a daily task. */}
            <Select
              value={taskType}
              onValueChange={(v) => {
                const next = v as TaskType;
                setTaskType(next);
                if (next === 'daily') setIsPriority(false);
              }}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {availableTypes.map((t) => <SelectItem key={t} value={t}>{TYPE_META[t].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Type-specific recurrence config */}
          {taskType === 'monthly' && (
            <div className="space-y-1.5">
              <Label>Select Date (1–28)</Label>
              <Select value={String(monthlyDay)} onValueChange={(v) => setMonthlyDay(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHLY_DAYS.map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {taskType === 'yearly' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Month</Label>
                <Select value={String(yearlyMonth)} onValueChange={(v) => setYearlyMonth(Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTH_LABELS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Select value={String(yearlyDay)} onValueChange={(v) => setYearlyDay(Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {yearlyDays.map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {taskType === 'weekly' && (
            <div className="space-y-1.5">
              <Label>Weekdays</Label>
              <DayChips options={WEEKDAY_OPTIONS} selected={weekdays} onChange={setWeekdays} />
              {errors.weekly_weekdays && <p className="text-xs text-rose-500">{errors.weekly_weekdays}</p>}
            </div>
          )}

          {taskType === 'periodic' && (
            <div className="space-y-3 rounded-lg border border-border/60 p-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Start Date <span className="text-rose-500">*</span></Label>
                  {/* A back-dated one-time task is dead on arrival: createTask seeds the
                      first occurrence from this date, and the daily reset immediately
                      closes anything already past. */}
                  <DatePicker
                    value={periodicStartDate}
                    minDate={todayIST}
                    onChange={(v) => {
                      setPeriodicStartDate(v);
                      // minDate cannot retract an End already chosen. Moving Start past it
                      // would otherwise leave a valid-looking range that fails on submit.
                      if (v && periodicEndDate && periodicEndDate < v) setPeriodicEndDate('');
                    }}
                  />
                  {errors.periodic_start_date && <p className="text-xs text-rose-500">{errors.periodic_start_date}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label>End Date <span className="text-rose-500">*</span></Label>
                  {/* Never before today, and never before Start once Start is picked. */}
                  <DatePicker
                    value={periodicEndDate}
                    minDate={periodicStartDate ? parseISO(periodicStartDate) : todayIST}
                    onChange={setPeriodicEndDate}
                  />
                  {errors.periodic_end_date && <p className="text-xs text-rose-500">{errors.periodic_end_date}</p>}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                <span className="text-rose-500 font-medium">*</span> Both Start Date and End Date must be selected before saving.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Start Time</Label>
              <TimePicker value={startTime} onChange={setStartTime} />
            </div>
            <div className="space-y-1.5">
              <Label>End Time</Label>
              <TimePicker value={endTime} onChange={setEndTime} />
            </div>
          </div>

          <div className="flex flex-wrap gap-5">
            {/* A daily task cannot be a priority task, so the option is not offered. */}
            {taskType !== 'daily' && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={isPriority} onChange={(e) => setIsPriority(e.target.checked)} className="h-4 w-4 accent-[hsl(var(--primary))]" />
                Priority Task
              </label>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={reminder} onChange={(e) => setReminder(e.target.checked)} className="h-4 w-4 accent-[hsl(var(--primary))]" />
              Enable Reminder
            </label>
          </div>

          <div className="rounded-lg border border-border/60 p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-foreground">Important Task</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Goes to one person at a time. If the first person in the sequence isn’t punched in,
                  it falls through to the next.
                </p>
                {/* Stated before submitting rather than discovered on rejection — with one
                    person there is nobody to fall through to. */}
                {isImportant && (
                  <p className="mt-1 text-xs font-medium text-amber-600 dark:text-amber-400">
                    Select at least {MIN_IMPORTANT_ASSIGNEES} people below.
                  </p>
                )}
              </div>
              <Switch checked={isImportant} onChange={setIsImportant} aria-label="Important Task" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>
              {isImportant ? 'Assignment Sequence' : 'Assign To'} <span className="text-rose-500">*</span>
            </Label>
            {isImportant && (
              <AssigneeSequenceList selected={assignees} onChange={setAssignees} />
            )}
            <AssigneePicker selected={assignees} onChange={setAssignees} />
            {errors.assignee_ids && <p className="text-xs text-rose-500">{errors.assignee_ids}</p>}
          </div>
        </div>

        <div className="flex items-center justify-end border-t border-border/60 pt-4 gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" onClick={submit} disabled={create.isPending}>
            <Save className="h-4 w-4" /> {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </DialogContent>

      <PriorityLimitDialog details={priorityLimit} onClose={() => setPriorityLimit(null)} />
    </Dialog>
  );
}
